import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { WorkspacePremium, Print, ContentCopy } from '@mui/icons-material';
import {
  issueCertificate,
  IssuedCertificate,
  IssueResult
} from '../../services/certificateService';
import './style.css';

/**
 * Certificate for a completed attempt (monetization feature #04).
 *
 * Issued on demand rather than automatically after every submission: most attempts are
 * practice runs nobody wants a certificate for, and issuing one per submission would make
 * the certificate worth less than the paper it is not printed on.
 *
 * Printing goes through the browser's own print dialog against a print stylesheet, not a
 * generated PDF. The artifact the student keeps is then identical to what they verified,
 * and there is no PDF renderer to ship, host or keep in sync with the design.
 */

interface Props {
  sessionId: string;
  /** Falls back to the name on the certificate when not supplied. */
  studentName?: string;
}

const CertificateSheet: React.FC<{
  certificate: IssuedCertificate;
  fallbackName?: string;
}> = ({ certificate, fallbackName }) => {
  const verifyUrl = `${window.location.origin}${certificate.verifyPath}`;

  return (
    <Box className="certificate-sheet">
      <Box className="certificate-border">
        <Typography className="certificate-eyebrow">Certificate of Participation</Typography>

        <Typography className="certificate-name">
          {certificate.studentName ?? fallbackName ?? 'Student'}
        </Typography>

        <Typography className="certificate-body">
          has completed{certificate.series ? ` the ${certificate.series}` : ' an'} assessment
          {certificate.percentage != null && (
            <>
              {' '}
              with a score of <b>{certificate.percentage.toFixed(1)}%</b>
              {certificate.score != null && certificate.totalScore != null && (
                <>
                  {' '}
                  ({certificate.score}/{certificate.totalScore})
                </>
              )}
            </>
          )}
          .
        </Typography>

        <Box className="certificate-footer">
          <Box>
            <Typography className="certificate-meta-label">Issued</Typography>
            <Typography className="certificate-meta-value">
              {new Date(certificate.issuedAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Box>
            <Typography className="certificate-meta-label">Verify at</Typography>
            <Typography className="certificate-meta-value certificate-hash">
              {verifyUrl}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const CertificatePanel: React.FC<Props> = ({ sessionId, studentName }) => {
  const [result, setResult] = useState<IssueResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleIssue = async () => {
    setLoading(true);
    setResult(await issueCertificate(sessionId));
    setLoading(false);
  };

  const handleCopyLink = async (certificate: IssuedCertificate) => {
    const url = `${window.location.origin}${certificate.verifyPath}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard access is denied in some embedded browsers. The link is on screen
      // either way, so this is not worth an error state.
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }} className="certificate-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }} className="no-print">
        <WorkspacePremium color="primary" />
        <Typography variant="h5">Your certificate</Typography>
      </Box>

      {result?.state !== 'ok' && (
        <Box className="no-print">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a certificate for this attempt. It carries a verification code anyone
            can check, so it stands up on a school application.
          </Typography>

          {result?.state === 'not-earned' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {result.message}
            </Alert>
          )}
          {result?.state === 'not-found' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This attempt could not be found.
            </Alert>
          )}
          {result?.state === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {result.message}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={handleIssue}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <WorkspacePremium />}
          >
            {loading ? 'Generating…' : 'Get my certificate'}
          </Button>
        </Box>
      )}

      {result?.state === 'ok' && (
        <>
          <CertificateSheet certificate={result.certificate} fallbackName={studentName} />

          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }} className="no-print">
            <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>
              Print or save as PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => handleCopyLink(result.certificate)}
            >
              Copy verification link
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        message="Verification link copied"
      />
    </Paper>
  );
};

export default CertificatePanel;
